import type { KeyboardEvent } from 'react';
import React, { useCallback } from 'react';
import FocusLock from 'react-focus-lock';
import type { ShowSearchAction, UpdateSearchAction, ResetSearchAction } from 'state/actions/search';
import { ScrollLock } from 'view/common/scroll-lock';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import Blanket from '@atlaskit/blanket';
import FadeIn from '@atlaskit/motion/fade-in';
import Portal from '@atlaskit/portal';
import { layers } from '@atlaskit/theme/constants';
import { isValidSearchTerm } from '../common';
import { SearchBar } from '../search-bar';
import { SearchResult } from '../search-result';
import { StyledContainer } from './styled';

export interface Props {
    isOpen: boolean;
    term: string;
    onSearch: () => UpdateSearchAction;
    onClose: () => ShowSearchAction;
    resetSearch: () => ResetSearchAction;
    onReturnFocus: (_: Element) => boolean;
}

export const SearchModal = ({
    isOpen,
    term,
    onSearch,
    onClose,
    resetSearch,
    onReturnFocus,
}: Props & WithAnalyticsEventsProps) => {
    const showSearchResults = isValidSearchTerm(term);
    const onKeyUp = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        },
        [onClose]
    );

    return (
        <FadeIn duration={200} entranceDirection="bottom">
            {(fadeInProps) =>
                isOpen && (
                    <Portal zIndex={layers.modal()}>
                        <StyledContainer aria-hidden={false} {...fadeInProps} onKeyUp={onKeyUp}>
                            {/*Disabling existing violations, should be fixed when revisited.*/}
                            {/*eslint-disable-next-line jsx-a11y/no-autofocus*/}
                            <FocusLock autoFocus returnFocus={onReturnFocus}>
                                {/* Ensures scroll events are blocked on the document body and locked */}
                                <ScrollLock />
                                <Blanket shouldAllowClickThrough={false} isTinted>
                                    <>
                                        <SearchBar
                                            term={term}
                                            onChange={onSearch}
                                            onClose={onClose}
                                            resetSearch={resetSearch}
                                        />
                                        {showSearchResults && <SearchResult term={term} />}
                                    </>
                                </Blanket>
                            </FocusLock>
                        </StyledContainer>
                    </Portal>
                )
            }
        </FadeIn>
    );
};
